import { useCallback, useEffect, useRef, useState } from 'react';
import { throttle } from 'throttle-debounce';

const SCROLL_THROTTLE = 100;
const RESIZE_DELAY = 500;
const HIDE_HEADER_AFTER = 35;

const getMaxScrollPosition = () =>
    Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
    ) - window.innerHeight;

const getClampedPageYOffset = () =>
    Math.min(Math.max(0, window.pageYOffset), getMaxScrollPosition());

export const useHeaderVisibility = () => {
    const [isHeaderVisible, setIsHeaderVisible] = useState(true);
    const [isHeaderAtTop, setIsHeaderAtTop] = useState(true);
    const previousScrollPosition = useRef(window.pageYOffset);
    const [isGradientShow, setIsGradientShow] = useState(true);
    const [isScrollingDown, setIsScrollingDown] = useState(false);

    const updateHeaderVisibilityState = useCallback(() => {
        // Unhandled edgecase, header will show up when slidedown manager scrolls up
        const pageYOffset = getClampedPageYOffset();
        const delta = pageYOffset - previousScrollPosition.current;
        setIsScrollingDown(delta > 0);

        setIsGradientShow(pageYOffset > HIDE_HEADER_AFTER || delta < 0);

        if (isScrollingDown && isHeaderVisible && pageYOffset > HIDE_HEADER_AFTER) {
            requestAnimationFrame(() => {
                setIsHeaderVisible(false);
            });
        } else if ((!isScrollingDown || HIDE_HEADER_AFTER > pageYOffset) && !isHeaderVisible) {
            requestAnimationFrame(() => {
                setIsHeaderVisible(true);
            });
        }

        const isReachedTop = pageYOffset === 0;
        if (isHeaderAtTop !== isReachedTop) {
            setIsHeaderAtTop(isReachedTop);
        }
        previousScrollPosition.current = pageYOffset;
    }, [isScrollingDown, isHeaderVisible, isHeaderAtTop]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const throttledOnScroll = useCallback(throttle(SCROLL_THROTTLE, updateHeaderVisibilityState), [
        updateHeaderVisibilityState,
    ]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const throttledOnResize = useCallback(
        throttle(RESIZE_DELAY, () => {
            setIsHeaderAtTop(Math.max(0, window.pageYOffset) === 0);
        }),
        []
    );

    useEffect(() => {
        window.addEventListener('scroll', throttledOnScroll, { passive: true });
        window.addEventListener('resize', throttledOnResize);

        return () => {
            window.removeEventListener('scroll', throttledOnScroll);
            window.removeEventListener('resize', throttledOnResize);
        };
    }, [throttledOnScroll, throttledOnResize]);

    const isGradientVisible = !isHeaderAtTop && isHeaderVisible && isGradientShow;

    return {
        isHeaderVisible,
        isGradientVisible,
    };
};
